/**
 * Canonical URL sent to Umami for a content page.
 *
 * Uses the stable page UUID rather than a URL path, so slug renames, locale
 * changes, and custom-domain rewrites all collapse to one row in analytics.
 * EAS queries Umami by path `/p/<uuid>` with no query params, so the URL
 * must match exactly to avoid misses in the path filter.
 */
export const buildCanonicalPageUrl = (pageId: string): string => `/p/${pageId}`;

/**
 * Rewrites an internal referrer so it carries the same stable hostname that
 * Umami receives for every pageview in this org.
 *
 * Once we fix `hostname` to a stable UUID-derived value, a resident arriving
 * from a hub on the org's custom domain has a referrer whose hostname differs
 * from our fake hostname — Umami would classify it as external referral and
 * inflate the referral channel. Replacing the hostname with the same stable
 * value marks the visit as internal navigation instead.
 *
 * External referrers (e.g. google.com, twitter.com) are returned unchanged.
 */
export const canonicalizeReferrer = (
  referrer: string,
  currentHostname: string,
  orgCanonicalHostname: string | null | undefined,
  stableHostname: string,
): string => {
  if (!referrer) return '';
  try {
    const url = new URL(referrer);
    const isInternal =
      url.hostname === currentHostname || (orgCanonicalHostname != null && url.hostname === orgCanonicalHostname);
    if (!isInternal) return referrer;
    url.hostname = stableHostname;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return referrer;
  }
};
